/**
 * MISSION 06V1-SKILL-08C: Research Intent Resolver
 * 
 * Bounded classifier for determining whether a Main Chat user query requires
 * live web research vs normal conversational execution.
 * 
 * Rule: Normal greetings, general educational questions, and local spatial queries
 * bypass web research to maintain fast conversational responses.
 */

export class ResearchIntentResolver {
  private static readonly EXPLICIT_RESEARCH_TRIGGERS: RegExp[] = [
    /\b(check\s+(this\s+|the\s+)?(website|web\s*page|site|page|url|link|article)|search\s+(the\s+)?web|search\s+online|look\s+up\s+(on\s+(the\s+)?web|online|on\s+the\s+internet)|(do\s+|conduct\s+)?(web\s+research|research\s+online|research\s+(on\s+)?(the\s+)?web)|find\s+information\s+(on\s+(the\s+)?web|online)|find\s+(online|web)\s+information|what('s|\s+is)\s+being\s+reported|get\s+me\s+news|fetch\s+news|read\s+news|show\s+news)\b/i,
    /\b(news\s+(from|regarding|about|on|in))\b/i,
    /\b(latest\s+on|current\s+(status|info|news|developments)|right\s+now|today's\s+news)\b/i,
    /\b(http:\/\/|https:\/\/)\b/i,
    /\b(check|read|get)\s+(cnn|hacker\s*news|w3c|bbc|reuters)\b/i,
    /\b(cnn|bbc|reuters)\s+news\b/i,
    /\bnews\s+from\s+(cnn|bbc|reuters)\b/i,
    /\bwhat's\s+happening\s+in\s+(ai|tech|news)\b/i,
    /\b(yes|yeah|sure|ok|okay)\s*,?\s*(get|fetch|check|read|show|find|news)\b/i,
    /\b(broader|broaden|other\s+sources|expand\s+search|try\s+more|different\s+sources)\b/i,
  ];

  private static readonly NORMAL_CHAT_TRIGGERS: RegExp[] = [
    /^(hello|hi|hey|greetings|good\s+morning|good\s+evening)\s*$/i,
    /^who\s+are\s+you/i,
    /^explain\s+.*like\s+i('m|\s+am)\s+\d+/i,
    /^what\s+is\s+a\s+(typescript|javascript|python|react|html|css|interface|class|function)\b/i,
    /^fly\s+to\b/i,
    /^navigate\s+to\b/i,
  ];

  /**
   * Evaluates whether a query requires live web research.
   */
  public isWebResearchRequested(query: string, context?: { conversationHistory?: string[] }): boolean {
    if (!query || typeof query !== 'string') {
      return false;
    }

    const trimmed = query.trim();
    if (trimmed.length === 0) {
      return false;
    }

    // 1. Check if research trigger matches first (to prevent "Hey Wingman, check CNN..." from being swallowed by greeting)
    for (const pattern of ResearchIntentResolver.EXPLICIT_RESEARCH_TRIGGERS) {
      if (pattern.test(trimmed)) {
        return true;
      }
    }

    // 2. Check context memory for follow-up research affirmative responses ("yes", "yeah", etc.)
    if (context?.conversationHistory && context.conversationHistory.length > 0) {
      const isAffirmative = /^(yes|yeah|sure|ok|okay|please)\b/i.test(trimmed);
      if (isAffirmative) {
        const lastTurn = context.conversationHistory[context.conversationHistory.length - 1] || '';
        if (/news|cnn|search|fetch|gather|research|headlines/i.test(lastTurn)) {
          return true;
        }
      }
    }

    // 3. Check if explicit normal chat trigger matches
    for (const pattern of ResearchIntentResolver.NORMAL_CHAT_TRIGGERS) {
      if (pattern.test(trimmed)) {
        return false;
      }
    }

    return false;
  }
}

